import axios from 'axios';
import { AppConfig } from 'config/api_config';
import { Auth } from 'aws-amplify';
import { default as Endpoints } from './apiEndPoints';
import { logout } from 'utils/utils';

// Map to store ongoing requests
const activeRequests = new Map();

// Function to generate a unique key for each request
const generateRequestKey = (method, url, data, baseURL, login) => {
  // Include login parameter in key generation since it affects the request
  const dataStr = data ? JSON.stringify(data) : '';
  return `${method}:${baseURL}${url}:${dataStr}:${login}`;
};

const makeRequest = async (
  method,
  url,
  data,
  baseURL,
  login = true,
  excludeFromLoading = false,
  cancelToken = null,
  preventDuplicates = true,
  extraHeaders = {}
) => {
  url = url.replaceAll('#', '%23');
  url = url.replaceAll('+', '%2B');

  // Generate unique key for this request
  const requestKey = generateRequestKey(method, url, data, baseURL, login);

  // Check if duplicate prevention is enabled and request is already in progress
  if (preventDuplicates && activeRequests.has(requestKey)) {
    return activeRequests.get(requestKey);
  }

  // Create the actual request function
  const executeRequest = async () => {
    const config = {
      method,
      url,
      baseURL,
      excludeFromLoading,
      cancelToken,
    };

    if (
      method === Endpoints.methodType.PUT ||
      method === Endpoints.methodType.PATCH ||
      method === Endpoints.methodType.POST ||
      method === Endpoints.methodType.DELETE
    ) {
      config.data = data;
    }

    if (method === Endpoints.methodType.GET) {
      config.params = data;
    }

    config.headers = {
      'Content-Type': 'application/json; charset=utf-8',
      ...extraHeaders,
    };

    const hasApiKey = extraHeaders?.hasOwnProperty('x-api-key');

    if (!login && hasApiKey) {
      config.headers.Authorization = null;
    }

    if (login) {
      let token;
      if (!excludeFromLoading) {
        typeof global?.setLoader === 'function' && global?.setLoader(true);
      }
      try {
        const authData = await Auth.currentSession();
        if (!excludeFromLoading) {
          typeof global?.setLoader === 'function' && global?.setLoader(false);
        }
        token = authData.getIdToken().getJwtToken();
        config.headers.Authorization = token;
      } catch (error) {
        if (!excludeFromLoading) {
          typeof global?.setLoader === 'function' && global?.setLoader(false);
        }
        logout();
        throw error;
      }
    }

    return axios.request(config);
  };

  // Create the request promise and store it immediately
  const requestPromise = executeRequest()
    .then((response) => {
      // Remove from active requests on success
      if (preventDuplicates) {
        activeRequests.delete(requestKey);
      }
      return response;
    })
    .catch((error) => {
      // Remove from active requests on error
      if (preventDuplicates) {
        activeRequests.delete(requestKey);
      }
      throw error;
    });

  // Store the promise if duplicate prevention is enabled
  if (preventDuplicates) {
    activeRequests.set(requestKey, requestPromise);
  }

  return requestPromise;
};

export const performRequest = async (method, url, data, _timeEntryData, login = true, excludeFromLoading = false) => {
  return makeRequest(method, url, data, AppConfig.baseURL, login, excludeFromLoading);
};

export const performRequestSecond = async (
  method,
  url,
  data,
  _timeEntryData,
  login = true,
  excludeFromLoading = false,
  extraHeaders = {}
) => {
  return makeRequest(method, url, data, AppConfig.baseURL2, login, excludeFromLoading, null, true, extraHeaders);
};

export const performRequestThird = async (
  method,
  url,
  data,
  _timeEntryData,
  login = true,
  excludeFromLoading = false,
  cancelToken = null
) => makeRequest(method, url, data, AppConfig.baseURL3, login, excludeFromLoading, cancelToken);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const { message } = error?.response || error;
    if (
      message === 'Refresh Token has expired' ||
      message === 'Invalid Refresh Token' ||
      message === 'Refresh Token has been revoked' ||
      message === 'No current user'
    ) {
      // window.open(`http://${process.env.REACT_APP_DOMAIN}/#/login`,"_self")

      logout();
    }
    return Promise.reject(error);
  }
);

export const performRequestFourth = async (
  method,
  url,
  data,
  _timeEntryData,
  login = true,
  excludeFromLoading = false,
  cancelToken = null
) => makeRequest(method, url, data, AppConfig.baseURL4, login, excludeFromLoading, cancelToken);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const { message } = error?.response || error;
    if (
      message === 'Refresh Token has expired' ||
      message === 'Invalid Refresh Token' ||
      message === 'Refresh Token has been revoked' ||
      message === 'No current user'
    ) {
      // window.open(`http://${process.env.REACT_APP_DOMAIN}/#/login`,"_self")

      logout();
    }
    return Promise.reject(error);
  }
);

export const axiosInstance = axios;
